import React, { useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { ROUTE } from "@/route/app_routes.js";
import { loginUser, verifyTwoFactorLogin } from "@/services/authService";
import { getErrorMessage } from "@/services/apiClient";
import { useProfile } from "@/context/ProfileContext";

const LoginForm = () => {

  const router = useRouter();
  const { t } = useTranslation();
  // ProfileContext (context/ProfileContext.js) fetches /api/auth/me exactly once, on the first
  // page load of the whole app session, and Next.js's Pages Router navigates from /login to the
  // dashboard client-side (no full reload) -- so without an explicit refresh here, the navbar
  // (HeaderRight/Offcanvas/User, both driven by that same shared context) would keep showing
  // "Log In" / stale data until the user's next full page load. Calling refreshProfile() right
  // after a session is actually established is what makes the navbar update immediately.
  const { refreshProfile } = useProfile();
  const [focusedInput, setFocusedInput] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState(null);
  // Set only when the backend's 403 explicitly says this account can appeal (a temporary
  // suspension, not a permanent ban) -- see AuthController::login()'s `can_appeal` flag.
  const [canAppeal, setCanAppeal] = useState(false);

  // Second-step state: accounts with two-factor authentication enabled (Settings' Security tab)
  // don't get a session from the password step alone -- see pages/api/auth/login.js, which
  // returns { two_factor_required: true, email } instead of setting the auth cookie. We stash
  // the email + "remember me" choice here so the code-entry step can finish the same login.
  const [twoFactor, setTwoFactor] = useState(null); // { email, remember } | null
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  // Built inside the component (not at module scope) so validation messages respond to the
  // active language -- zod schemas can't call the useTranslation() hook themselves.
  const loginSchema = useMemo(
    () =>
      z.object({
        email: z.string().min(1, t("auth.validation.emailRequired")).email(t("auth.validation.emailInvalid")),
        password: z.string().min(1, t("auth.validation.passwordRequired")),
        remember: z.boolean().optional(),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(loginSchema),
  });

  const goToDashboard = () => {
    const returnTo = typeof router.query.returnTo === "string" ? router.query.returnTo : ROUTE.dashboard;
    router.push(returnTo);
  };

  const onSubmit = async (values) => {
    setServerError(null);
    setCanAppeal(false);
    try {
      const res = await loginUser(values);
      if (res.two_factor_required) {
        setTwoFactor({ email: res.email || values.email, remember: !!values.remember });
        toast.success(res.message || t("auth.login.verificationSentToast"));
        return;
      }
      toast.success(t("auth.login.welcomeBackToast"));
      await refreshProfile();
      goToDashboard();
    } catch (error) {
      const message = getErrorMessage(error);
      setServerError(message);
      setCanAppeal(!!error?.response?.data?.errors?.can_appeal);
      toast.error(message);
    }
  };

  const onVerifyTwoFactor = async (e) => {
    e.preventDefault();
    setServerError(null);
    setVerifying(true);
    try {
      await verifyTwoFactorLogin({ email: twoFactor.email, code, remember: twoFactor.remember });
      toast.success(t("auth.login.welcomeBackToast"));
      await refreshProfile();
      goToDashboard();
    } catch (error) {
      const message = getErrorMessage(error);
      setServerError(message);
      toast.error(message);
    } finally {
      setVerifying(false);
    }
  };

  if (twoFactor) {
    return (
      <div className="rbt-auth-split__form-card">
        <div className="rbt-auth-split__welcome">
          <h1 className="title">{t("auth.login.enterCode")}</h1>
          <p>{t("auth.login.codeInstructions")}</p>
        </div>

        {serverError && (
          <div className="rbt-auth-split__alert" role="alert">
            <i className="feather-alert-circle"></i>
            <span>{serverError}</span>
          </div>
        )}

        <form onSubmit={onVerifyTwoFactor} noValidate>
          <div className="rbt-auth-split__field">
            <div className={`form-group ${focusedInput === "code" ? "focused" : ""}`}>
              <i className="feather-shield rbt-auth-split__field-icon"></i>
              <input
                id="two-factor-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder=" "
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onFocus={() => setFocusedInput("code")}
                onBlur={() => setFocusedInput(null)}
              />
              <label htmlFor="two-factor-code">{t("auth.login.codeLabel")}</label>
              <span className="focus-border"></span>
            </div>
          </div>

          <div className="form-submit-group">
            <button type="submit" className="rbt-btn btn-md btn-gradient hover-icon-reverse w-100" disabled={verifying || code.length !== 6}>
              <span className="icon-reverse-wrapper">
                <span className="btn-text">
                  {verifying && <span className="rbt-auth-split__spinner" aria-hidden="true" />}
                  {verifying ? t("auth.login.verifying") : t("auth.login.verifyAndSignIn")}
                </span>
                <span className="btn-icon"> <i className="feather-arrow-right"></i> </span>
                <span className="btn-icon"> <i className="feather-arrow-right"></i> </span>
              </span>
            </button>
          </div>

          <div className="text-center mt--20">
            <button type="button" className="rbt-btn-link" onClick={() => { setTwoFactor(null); setCode(""); setServerError(null); }}>
              {t("auth.login.backToLogin")}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (

    <>
      <div className="rbt-auth-split__form-card">

        <div className="rbt-auth-split__welcome">
          <h1 className="title">{t("auth.login.welcomeBack")}</h1>
          <p>{t("auth.login.subheading")}</p>
        </div>

        {serverError && (
          <div className="rbt-auth-split__alert" role="alert">
            <i className="feather-alert-circle"></i>
            <span>{serverError}</span>
          </div>
        )}
        {canAppeal && (
          <p className="text-center mb--20">
            <Link className="rbt-btn-link" href={ROUTE.appeal + "?type=account_suspension"}>
              Submit an appeal <i className="feather-arrow-right"></i>
            </Link>
          </p>
        )}

        <form onSubmit={handleSubmit(onSubmit)} noValidate>

          <div className="rbt-auth-split__field">
            <div className={`form-group ${ focusedInput === "email" ? "focused" : "" }`}>
              <i className="feather-mail rbt-auth-split__field-icon"></i>
              <input
                id="email"
                type="email"
                placeholder=" "
                autoComplete="email"
                {...register("email")}
                onFocus={() => setFocusedInput("email")}
                onBlur={(e) => { setFocusedInput(null); register("email").onBlur(e); }}
              />
              <label htmlFor="email">{t("auth.emailAddress")}</label>
              <span className="focus-border"></span>
              {errors.email && <span className="text-danger d-block mt--5">{errors.email.message}</span>}
            </div>
          </div>

          <div className="rbt-auth-split__field">
            <div className={`form-group rbt-password-group ${ focusedInput === "password" ? "focused" : "" }`}>
              <i className="feather-lock rbt-auth-split__field-icon"></i>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder=" "
                autoComplete="current-password"
                {...register("password")}
                onFocus={() => setFocusedInput("password")}
                onBlur={(e) => { setFocusedInput(null); register("password").onBlur(e); }}
              />
              <label htmlFor="password">{t("auth.password")}</label>
              <button
                type="button"
                className="rbt-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
                aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
              >
                <i className={showPassword ? "feather-eye-off" : "feather-eye"}></i>
              </button>
              <span className="focus-border"></span>
              {errors.password && <span className="text-danger d-block mt--5">{errors.password.message}</span>}
            </div>
          </div>

          <div className="row mb--30">
            <div className="col-6">
              <div className="rbt-checkbox">
                <input type="checkbox" id="rememberme" {...register("remember")} />
                <label htmlFor="rememberme">{t("auth.login.rememberMe")}</label>
              </div>
            </div>
            <div className="col-6">
              <div className="rbt-lost-password text-end">
                <Link className="rbt-btn-link" href={ROUTE.forgotPassword}>{t("auth.login.forgotPassword")}</Link>
              </div>
            </div>
          </div>

          <div className="form-submit-group">
            <button type="submit" className="rbt-btn btn-md btn-gradient hover-icon-reverse w-100" disabled={isSubmitting}>
              <span className="icon-reverse-wrapper">
                <span className="btn-text">
                  {isSubmitting && <span className="rbt-auth-split__spinner" aria-hidden="true" />}
                  {isSubmitting ? t("auth.signingIn") : t("auth.signIn")}
                </span>
                <span className="btn-icon">
                  <i className="feather-arrow-right"></i>
                </span>
                <span className="btn-icon">
                  <i className="feather-arrow-right"></i>
                </span>
              </span>
            </button>
          </div>

          <p className="rbt-auth-split__legal">
            {t("auth.login.legalPrefix")}{" "}
            <Link href={ROUTE.terms}>{t("auth.login.terms")}</Link> {t("auth.login.and")} <Link href={ROUTE.privacy}>{t("auth.login.privacyPolicy")}</Link>.
          </p>

        </form>
      </div>
    </>

  );

};

export default LoginForm;
